import React, { useMemo, useState } from 'react';
import { Form, Select } from 'antd';
import { useGetSelectFieldValues } from '@/shared/api/client/field-configuration-controller';
import type { SelectFieldOption } from '@/shared/api/model';
import type { ReportParameter } from '../../types';

export type ProductType = 'S' | 'H' | 'L' | 'F';

export interface ProductSelectProps {
  param: ReportParameter;
  productType: ProductType;
  disabled?: boolean;
  placeholder?: string;
  form: any;
  reportCode?: string;
  reportCategory?: string;
  value?: string;
  onChange?: (value: string | undefined) => void;
  onBlur?: () => void;
}

const PRODUCT_LABELS: Record<ProductType, string> = {
  S: 'Savings Product',
  H: 'Shares Product',
  L: 'Loan Product',
  F: 'Fixed Deposit Product',
};

/**
 * Generic product select component that filters by product type.
 * Uses the 'products' provider with productType parameter.
 *
 * Product types:
 * - S = Savings
 * - H = Shares
 * - L = Loans
 * - F = Fixed Deposits
 *
 * Used for parameter types: SAVINGS_PRODUCT_SELECT, SHARES_PRODUCT_SELECT,
 * LOAN_PRODUCT_SELECT, FIXED_DEPOSIT_PRODUCT_SELECT
 */
const ProductSelect: React.FC<ProductSelectProps> = ({
  param,
  productType,
  disabled,
  placeholder,
  form,
  reportCode,
  reportCategory,
  value,
  onChange,
  onBlur,
}) => {
  const [search, setSearch] = useState<string | undefined>(undefined);

  const branchValue = Form.useWatch('branch', form);

  const params = useMemo(() => {
    const p: Record<string, unknown> = {
      search,
      fieldName: param.name,
      entityType: 'REPORT',
      reportCode,
      reportCategory,
      productType,
    };

    if (branchValue) {
      p.branch = branchValue;
    }

    return p;
  }, [search, param.name, branchValue, reportCode, reportCategory, productType]);

  const {
    data: options,
    isLoading,
    error,
  } = useGetSelectFieldValues('products', params as any, {
    query: {
      staleTime: 5 * 60 * 1000,
    },
  });

  const selectOptions = useMemo(() => {
    const list: SelectFieldOption[] = Array.isArray(options) ? options : [];
    const mapped = list.map((opt) => ({
      value: opt.value,
      label: opt.label,
      disabled: opt.disabled,
    }));

    const hasAll = mapped.some((o) => String(o.value) === '-1');
    if (!hasAll && (param.defaultValue === -1 || param.defaultValue === '-1')) {
      mapped.unshift({ value: '-1', label: `All ${PRODUCT_LABELS[productType]}s`, disabled: undefined });
    }

    return mapped;
  }, [options, param.defaultValue, productType]);

  const defaultPlaceholder = placeholder || `Select ${PRODUCT_LABELS[productType].toLowerCase()}`;

  return (
    <Select
      placeholder={defaultPlaceholder}
      disabled={disabled}
      loading={isLoading}
      showSearch
      allowClear
      filterOption={false}
      onSearch={(q) => setSearch(q)}
      options={selectOptions}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      notFoundContent={
        error
          ? `Failed to load ${PRODUCT_LABELS[productType].toLowerCase()}s`
          : isLoading
            ? 'Loading...'
            : `No ${PRODUCT_LABELS[productType].toLowerCase()}s`
      }
      optionFilterProp="label"
    />
  );
};

export default ProductSelect;
